<template>
  <div class="check-lab-definition">
    <div v-if="loading && !definition" class="state-msg">Loading this project’s check…</div>
    <div v-else-if="error" class="state-msg error">{{ error }}</div>

    <form v-else-if="definition" class="definition-form" @submit.prevent="saveDefinition">
      <section class="purpose-cell">
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-clipboard-check"></i>
        </div>
        <div class="purpose-body">
          <h2>{{ definition.name || 'Untitled check' }}</h2>
          <p>
            Settings for
            <strong>{{ definition.name || 'this comprehensive check' }}</strong>
            — the requirements this project reviews certificates against.
            Column cell policy applies to every ADDL INSD / SUBR WVD rule on the check.
          </p>
        </div>
      </section>

      <div v-if="saveMessage" class="save-notification" :class="`save-notification-${saveMessageType}`">
        <i :class="saveMessageType === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'"></i>
        {{ saveMessage }}
      </div>

      <div class="fields-grid">
        <div class="field">
          <label for="lab-def-name">Check name</label>
          <input id="lab-def-name" v-model="definition.name" type="text" required />
        </div>
        <div class="field">
          <label for="lab-def-project-type">Project type</label>
          <select id="lab-def-project-type" v-model="definition.project_type" required>
            <option v-for="ptype in projectTypes" :key="ptype.value" :value="ptype.value">
              {{ ptype.label }}
            </option>
          </select>
        </div>
        <div class="field">
          <label for="lab-def-app-type">Application type</label>
          <select id="lab-def-app-type" v-model="definition.check_application_type" required>
            <option v-for="atype in applicationTypes" :key="atype.value" :value="atype.value">
              {{ atype.label }}
            </option>
          </select>
          <p class="field-help">Which analysis handler runs when a document is reviewed.</p>
        </div>
        <div v-if="definition.check_application_type === 'COI_COMPLIANCE'" class="field">
          <label for="lab-def-column-policy">Column cell policy</label>
          <select id="lab-def-column-policy" v-model="definition.column_cell_policy">
            <option value="verify_in_force">
              Verify in force — blank OK if other evidence (franchise / JMFS)
            </option>
            <option value="strict_certificate">
              Strict certificate — boxes must be marked (GC / Builders)
            </option>
          </select>
          <p class="field-help">
            Strict certificate fails a blank ADDL INSD / SUBR WVD box even when Description of
            Operations mentions a blanket endorsement. Test a cert in Prompt Editor after saving.
          </p>
        </div>
        <div class="field field-span">
          <label for="lab-def-description">Description</label>
          <textarea id="lab-def-description" v-model="definition.description" rows="2" />
        </div>
      </div>

      <div class="roster-actions">
        <button
          v-if="definition.check_application_type === 'COI_COMPLIANCE'"
          type="button"
          class="btn btn-outline"
          @click="addCOIStandardRequirements"
        >
          <i class="fas fa-shield-alt"></i> Add COI starter pack
        </button>
        <button type="button" class="btn btn-outline" @click="openCarrierUploadModal">
          <i class="fas fa-file-upload"></i> Extract rules from a carrier document
        </button>
        <button
          type="button"
          class="btn btn-outline"
          :disabled="validating || !definitionId"
          @click="validateDefinition"
        >
          <i :class="validating ? 'fas fa-spinner fa-spin' : 'fas fa-stethoscope'"></i>
          {{ validating ? 'Validating…' : 'Validate check' }}
        </button>
      </div>

      <div v-if="validationResult" class="validation-panel">
        <div class="validation-head">
          <strong>{{ validationResult.score }}/100</strong>
          <span>{{ validationResult.requirement_count }} active rules</span>
          <button type="button" class="text-btn" @click="validationResult = null">Dismiss</button>
        </div>
        <p v-if="validationResult.summary">{{ validationResult.summary }}</p>
        <ul v-if="validationResult.issues?.length">
          <li v-for="(issue, idx) in validationResult.issues" :key="idx">
            <strong>{{ issue.type }}</strong> — {{ issue.description }}
          </li>
        </ul>
        <p v-else-if="validationResult.issues && !validationResult.issues.length" class="ok">
          No issues found.
        </p>
      </div>

      <div class="roster-grid">
        <section class="roster-col">
          <h3>Available automated checks</h3>
          <p class="field-help">Add a catalog check to this project’s comprehensive check.</p>
          <AvailableChecksPanel @add-check="handleAddCheck" />
        </section>
        <section class="roster-col">
          <h3>Rules on this check</h3>
          <p class="field-help">Grouped by coverage policy. Open a rule from Working on to edit its prompt.</p>
          <SelectedRequirementsPanel
            :requirements="definition.requirements"
            @update-requirements="handleUpdateRequirements"
          />
        </section>
      </div>

      <div class="form-footer">
        <button type="submit" class="btn btn-primary" :disabled="loading">
          {{ loading ? 'Saving…' : 'Save check settings' }}
        </button>
      </div>
    </form>

    <div v-if="showCarrierUploadModal" class="modal-overlay" @click.self="closeCarrierUploadModal">
      <div class="modal-container" role="dialog" aria-labelledby="carrier-extract-title">
        <div class="modal-header">
          <h3 id="carrier-extract-title">
            <i class="fas fa-file-upload"></i>
            Extract rules from a carrier document
          </h3>
          <button type="button" class="modal-close" aria-label="Close" @click="closeCarrierUploadModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <p class="field-help">
            Upload a carrier requirements PDF to draft rules for
            <strong>{{ definition?.name || 'this check' }}</strong>.
            Review the extract, then save the check to persist them.
          </p>
          <div v-if="!extractedRequirements.length" class="upload-form">
            <div class="field">
              <label>Carrier requirements document</label>
              <div
                class="file-upload-area"
                :class="{ dragging: isDragging }"
                @dragover.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false"
                @drop.prevent="handleCarrierFileDrop"
              >
                <input
                  ref="carrierFileInput"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  class="sr-only"
                  @change="handleCarrierFileSelect"
                />
                <div v-if="!carrierUpload.file" class="upload-placeholder" @click="carrierFileInput?.click()">
                  <i class="fas fa-cloud-upload-alt"></i>
                  <p>Drop a carrier requirements PDF, or click to choose</p>
                  <small>Reviewed against this check after you save</small>
                </div>
                <div v-else class="file-selected">
                  <p><strong>{{ carrierUpload.file.name }}</strong></p>
                  <small>{{ formatFileSize(carrierUpload.file.size) }}</small>
                  <button type="button" class="btn btn-sm" @click="clearCarrierFile">Remove</button>
                </div>
              </div>
            </div>
            <div class="field">
              <label for="carrier-name">Carrier name</label>
              <input id="carrier-name" v-model="carrierUpload.carrierName" type="text" placeholder="e.g. Verus Insurance" />
            </div>
            <div class="field">
              <label for="carrier-doc-type">Document type</label>
              <select id="carrier-doc-type" v-model="carrierUpload.documentType">
                <option value="subcontractor_requirements">Subcontractor requirements (COI)</option>
                <option value="driver_safety_policy">Driver safety policy</option>
                <option value="employment_policy">Employment policy</option>
                <option value="safety_manual">Safety manual</option>
                <option value="procedure_manual">Procedure manual</option>
              </select>
            </div>
            <div v-if="carrierUploadError" class="state-msg error">{{ carrierUploadError }}</div>
          </div>
          <div v-else class="requirements-preview">
            <h4>Extracted {{ extractedRequirements.length }} rules</h4>
            <div v-for="(req, idx) in extractedRequirements" :key="idx" class="extract-card">
              <strong>{{ req.name }}</strong>
              <span class="code">{{ req.code }}</span>
              <p>{{ req.description }}</p>
              <button type="button" class="text-btn" @click="removeExtractedRequirement(idx)">Remove</button>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            v-if="!extractedRequirements.length"
            type="button"
            class="btn btn-primary"
            :disabled="!carrierUpload.file || !carrierUpload.carrierName || generatingFromCarrier"
            @click="generateChecksFromCarrierDoc"
          >
            {{ generatingFromCarrier ? 'Extracting…' : 'Extract rules' }}
          </button>
          <button v-else type="button" class="btn btn-primary" @click="saveExtractedRequirements">
            Add {{ extractedRequirements.length }} rules to this check
          </button>
          <button type="button" class="btn btn-ghost" @click="closeCarrierUploadModal">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import api from '@/services/api';
import AvailableChecksPanel from '@/components/comprehensive/AvailableChecksPanel.vue';
import SelectedRequirementsPanel from '@/components/comprehensive/SelectedRequirementsPanel.vue';

const props = defineProps({
  definitionId: { type: [Number, String], required: true },
});

const emit = defineEmits(['saved']);

const definition = ref(null);
const loading = ref(false);
const error = ref(null);
const saveMessage = ref(null);
const saveMessageType = ref('success');
const validating = ref(false);
const validationResult = ref(null);
let nextTempId = 0;

const showCarrierUploadModal = ref(false);
const extractedRequirements = ref([]);
const generatingFromCarrier = ref(false);
const carrierUploadError = ref('');
const isDragging = ref(false);
const carrierFileInput = ref(null);
const carrierUpload = ref({
  file: null,
  carrierName: '',
  documentType: 'subcontractor_requirements',
  applicationType: 'COI_COMPLIANCE',
});

const projectTypes = [
  { value: 'GENERAL', label: 'General Document Review' },
  { value: 'RISK_REVIEW_INS', label: 'Insurance Risk Review' },
  { value: 'CERT_REVIEW', label: 'Certificate Review' },
  { value: 'TAX_1099', label: '1099 Review' },
  { value: 'SCOPE_OF_WORK', label: 'Scope of Work Review' },
  { value: 'COMPLIANCE', label: 'Compliance Monitoring' },
  { value: 'HEALTHCARE_CREDENTIALING', label: 'Healthcare Provider Credentialing' },
  { value: 'MEDICAID_ENROLLMENT', label: 'Medicaid Provider Enrollment' },
];

const applicationTypes = [
  { value: 'COI_COMPLIANCE', label: 'Certificate of Insurance Compliance' },
  { value: 'HEALTHCARE_CREDENTIALING', label: 'Healthcare Provider Credentialing' },
  { value: 'MEDICAID_ENROLLMENT', label: 'Medicaid Provider Enrollment' },
  { value: 'POLICY_REVIEW', label: 'Policy/Procedure Document Review' },
  { value: 'CONTRACT_REVIEW', label: 'Contract Document Review' },
  { value: 'REGULATORY_COMPLIANCE', label: 'Regulatory Compliance Review' },
  { value: 'GENERAL', label: 'General Document Analysis' },
];

const showSaveMessage = (message, type = 'success') => {
  saveMessage.value = message;
  saveMessageType.value = type;
  setTimeout(() => { saveMessage.value = null; }, 4000);
};

const withTempIds = (requirements) => (requirements || []).map((r) => ({
  ...r,
  tempId: r.id || r.tempId || `temp-${nextTempId++}`,
}));

async function fetchDefinition() {
  loading.value = true;
  error.value = null;
  try {
    const response = await api.get(`/comprehensive-check-definitions/${props.definitionId}/`);
    definition.value = response.data;
    if (!definition.value.column_cell_policy) {
      definition.value.column_cell_policy = 'verify_in_force';
    }
    definition.value.requirements = withTempIds(definition.value.requirements);
  } catch (err) {
    error.value = err.response?.status === 404
      ? 'Check definition not found.'
      : 'Failed to load this check.';
  } finally {
    loading.value = false;
  }
}

function handleAddCheck(automatedCheck) {
  if (!definition.value) return;
  definition.value.requirements.push({
    tempId: `temp-${nextTempId++}`,
    code: automatedCheck.id ? `ACHK_${automatedCheck.id}` : `CUSTOM_${definition.value.requirements.length}`,
    name: automatedCheck.name,
    description: automatedCheck.description,
    automated_check: automatedCheck.id,
    automated_check_details: { name: automatedCheck.name },
    order: definition.value.requirements.length,
    severity: automatedCheck.severity || 'MEDIUM',
    validation_logic: automatedCheck.requirements_config || {},
    remediation_instructions: automatedCheck.remediation_instructions || '',
    is_active: true,
    applicable_document_types: [],
    applicable_coverage_types: [],
    require_classification: false,
  });
}

function handleUpdateRequirements(updatedRequirements) {
  if (!definition.value) return;
  definition.value.requirements = updatedRequirements.map((req, index) => ({
    ...req,
    order: index,
  }));
}

async function validateDefinition() {
  if (!props.definitionId) return;
  validating.value = true;
  validationResult.value = null;
  try {
    const response = await api.post(
      `/comprehensive-check-definitions/${props.definitionId}/validate_requirements/`,
    );
    if (response.data.success) {
      validationResult.value = response.data;
    } else {
      showSaveMessage(response.data.error || 'Validation failed', 'error');
    }
  } catch (err) {
    showSaveMessage(err.response?.data?.error || 'Failed to validate check', 'error');
  } finally {
    validating.value = false;
  }
}

async function saveDefinition() {
  if (!definition.value?.name?.trim()) {
    error.value = 'Check name is required.';
    return;
  }
  loading.value = true;
  error.value = null;
  const payload = {
    ...definition.value,
    requirements: definition.value.requirements.map((req) => {
      const requirementPayload = { ...req };
      delete requirementPayload.tempId;
      delete requirementPayload.automated_check_details;
      return requirementPayload;
    }),
  };
  try {
    const response = await api.put(
      `/comprehensive-check-definitions/${props.definitionId}/`,
      payload,
    );
    definition.value = response.data;
    definition.value.requirements = withTempIds(definition.value.requirements);
    showSaveMessage('Check settings saved');
    emit('saved', response.data);
  } catch (err) {
    error.value = err.response?.data
      ? `Failed to save: ${JSON.stringify(err.response.data)}`
      : 'Failed to save check settings.';
  } finally {
    loading.value = false;
  }
}

function addCOIStandardRequirements() {
  if (!definition.value) return;
  const existing = new Set(definition.value.requirements.map((r) => r.code));
  const startOrder = definition.value.requirements.length;
  const standards = [
    {
      code: 'COI_GL_LIMITS',
      name: 'General Liability Coverage Limits',
      description: 'Verify GL occurrence and aggregate limits meet requirements',
      severity: 'CRITICAL',
      applicable_document_types: ['CERTIFICATE'],
      applicable_coverage_types: ['GENERAL_LIABILITY'],
      prompt_type: 'coverage_amount',
      prompt_template: 'Verify that the General Liability coverage on this certificate meets the required limits. Check the "Each Occurrence" limit (minimum $1,000,000) and the "General Aggregate" limit (minimum $2,000,000). Report the exact values found, whether limits are met, and any deficiencies.',
    },
    {
      code: 'COI_WC_STATUTORY',
      name: 'Workers Compensation Statutory Limits',
      description: 'Verify WC is marked "Per Statute" or has statutory coverage',
      severity: 'CRITICAL',
      applicable_document_types: ['CERTIFICATE'],
      applicable_coverage_types: ['WORKERS_COMP'],
      prompt_type: 'checkbox_verification',
      prompt_template: 'Verify that Workers Compensation coverage on this ACORD 25 certificate shows statutory limits. Look for a checked "Per Statute" box or the text "Statutory" in the WC section. This is a visual checkbox check — report whether the Per Statute indicator is marked and note the exact text found in the WC limits section.',
    },
    {
      code: 'COI_AL_LIMITS',
      name: 'Auto Liability Coverage Limits',
      description: 'Verify auto liability combined single limit',
      severity: 'HIGH',
      applicable_document_types: ['CERTIFICATE'],
      applicable_coverage_types: ['AUTO_LIABILITY'],
      prompt_type: 'coverage_amount',
      prompt_template: 'Verify that the Auto Liability coverage on this certificate meets the required combined single limit of $1,000,000. Report the exact limit value found and whether it meets the requirement.',
    },
    {
      code: 'COI_AI_ENDORSEMENT',
      name: 'Additional Insured Endorsement',
      description: 'Verify additional insured status is endorsed, not just checked',
      severity: 'HIGH',
      applicable_document_types: ['CERTIFICATE'],
      applicable_coverage_types: [],
      prompt_type: 'entity_verification',
      prompt_template: 'Verify that Additional Insured (AI) status is properly documented on this certificate. Look for: (1) endorsement codes CG 2010, CG 2037, or similar in the description, (2) explicit "Additional Insured" language in the description of operations section, or (3) a separate endorsement attached. Report what evidence of AI status was found and whether it is adequately documented beyond just a checked box.',
    },
    {
      code: 'COI_WOS_ENDORSEMENT',
      name: 'Waiver of Subrogation Endorsement',
      description: 'Verify waiver of subrogation is documented with endorsement code',
      severity: 'HIGH',
      applicable_document_types: ['CERTIFICATE'],
      applicable_coverage_types: [],
      prompt_type: 'entity_verification',
      prompt_template: 'Verify that a Waiver of Subrogation (WOS) is properly documented on this certificate. Look for: (1) endorsement code CG 2404 or similar, (2) "Waiver of Subrogation" language in the description of operations, or (3) WOS checkboxes marked in the GL, AL, or WC sections. Report what evidence was found.',
    },
    {
      code: 'COI_EXPIRATION',
      name: 'Certificate Expiration Date',
      description: 'Verify certificate is not expired and coverage dates are valid',
      severity: 'CRITICAL',
      applicable_document_types: ['CERTIFICATE'],
      applicable_coverage_types: [],
      prompt_type: 'date_check',
      prompt_template: 'Check the expiration dates on this certificate of insurance. For each coverage line, find the policy expiration date (right column of the date range). Report all expiration dates found and identify any policies that are expired or expiring within 30 days. On ACORD 25 forms, the left date column is the effective date and the right is the expiration date.',
      requires_temporal_context: true,
    },
  ];
  let added = 0;
  standards.forEach((std, idx) => {
    if (existing.has(std.code)) return;
    definition.value.requirements.push({
      tempId: `temp-coi-${nextTempId++}`,
      code: std.code,
      name: std.name,
      description: std.description,
      severity: std.severity,
      order: startOrder + idx,
      prompt_template: std.prompt_template,
      validation_logic: { prompt_type: std.prompt_type },
      applicable_document_types: std.applicable_document_types,
      applicable_coverage_types: std.applicable_coverage_types,
      requires_temporal_context: std.requires_temporal_context || false,
      is_active: true,
      require_classification: false,
    });
    added += 1;
  });
  if (added === 0) {
    showSaveMessage('COI starter pack rules are already on this check', 'error');
  } else {
    showSaveMessage(`Added ${added} COI starter rule${added === 1 ? '' : 's'} — save to persist`);
  }
}

function openCarrierUploadModal() {
  showCarrierUploadModal.value = true;
}

function closeCarrierUploadModal() {
  showCarrierUploadModal.value = false;
  extractedRequirements.value = [];
  generatingFromCarrier.value = false;
  carrierUploadError.value = '';
  isDragging.value = false;
  carrierUpload.value = {
    file: null,
    carrierName: '',
    documentType: 'subcontractor_requirements',
    applicationType: 'COI_COMPLIANCE',
  };
}

function handleCarrierFileSelect(event) {
  const file = event.target.files?.[0];
  if (file) carrierUpload.value.file = file;
}

function handleCarrierFileDrop(event) {
  isDragging.value = false;
  const file = event.dataTransfer?.files?.[0];
  if (file) carrierUpload.value.file = file;
}

function clearCarrierFile() {
  carrierUpload.value.file = null;
  if (carrierFileInput.value) carrierFileInput.value.value = '';
}

function removeExtractedRequirement(idx) {
  extractedRequirements.value.splice(idx, 1);
}

function formatFileSize(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
}

async function generateChecksFromCarrierDoc() {
  if (!carrierUpload.value.file || !carrierUpload.value.carrierName) return;
  generatingFromCarrier.value = true;
  carrierUploadError.value = '';
  extractedRequirements.value = [];
  try {
    const formData = new FormData();
    formData.append('file', carrierUpload.value.file);
    formData.append('carrier_name', carrierUpload.value.carrierName);
    formData.append('document_type', carrierUpload.value.documentType);
    formData.append('application_type', carrierUpload.value.applicationType);
    const response = await api.post('/carrier-extraction/generate/', formData);
    if (response.data.requirements?.length) {
      extractedRequirements.value = response.data.requirements;
    } else {
      carrierUploadError.value = 'No rules were extracted from the document.';
    }
  } catch (err) {
    carrierUploadError.value =
      err.response?.data?.error || err.response?.data?.detail || 'Failed to extract rules.';
  } finally {
    generatingFromCarrier.value = false;
  }
}

function saveExtractedRequirements() {
  if (!extractedRequirements.value.length || !definition.value) return;
  const startOrder = definition.value.requirements.length;
  extractedRequirements.value.forEach((req, idx) => {
    definition.value.requirements.push({
      tempId: `temp-carrier-${nextTempId++}`,
      code: req.code || `CARRIER_${Math.random().toString(36).slice(2, 7).toUpperCase()}`,
      name: req.name || 'Extracted Requirement',
      description: req.description || '',
      severity: req.severity || 'HIGH',
      order: startOrder + idx,
      validation_logic: {
        prompt_template: req.validation_logic?.prompt_template || req.prompt_template || '',
      },
      remediation_instructions: req.remediation_instructions || '',
      is_active: true,
      applicable_document_types: req.applicable_document_types || ['CERTIFICATE'],
      applicable_coverage_types: [],
      require_classification: false,
    });
  });
  showSaveMessage(`Added ${extractedRequirements.value.length} extracted rules — save to persist`);
  closeCarrierUploadModal();
}

onMounted(fetchDefinition);
watch(() => props.definitionId, fetchDefinition);
</script>

<style scoped>
.check-lab-definition {
  height: 100%;
  overflow: auto;
  padding: 1.25rem 1.5rem 2rem;
  background: #f8fafc;
}

.definition-form {
  max-width: 72rem;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.purpose-cell {
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  padding: 1rem 1.1rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
}

.purpose-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.55rem;
  background: #f1f5f9;
  color: #334155;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.purpose-body h2 {
  margin: 0 0 0.25rem;
  font-size: 1.05rem;
}

.purpose-body p {
  margin: 0;
  font-size: 0.875rem;
  color: #475569;
  line-height: 1.45;
}

.fields-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;
  padding: 1rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
}

.field label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 0.3rem;
}

.field input,
.field select,
.field textarea {
  width: 100%;
  padding: 0.45rem 0.65rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.45rem;
  font-size: 0.875rem;
}

.field-help {
  margin: 0.3rem 0 0;
  font-size: 0.75rem;
  color: #64748b;
  line-height: 1.4;
}

.field-span { grid-column: 1 / -1; }

.roster-actions,
.form-footer {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.roster-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.roster-col {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1rem;
}

.roster-col h3 {
  margin: 0 0 0.25rem;
  font-size: 0.95rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.85rem;
  border-radius: 0.45rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
}

.btn-primary { background: var(--brand-accent, #2563eb); color: #fff; }
.btn-outline { background: #fff; border-color: #cbd5e1; color: #334155; }
.btn-ghost { background: #e2e8f0; color: #334155; }
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn-sm { font-size: 0.78rem; padding: 0.25rem 0.55rem; }

.text-btn {
  background: none;
  border: none;
  color: #2563eb;
  font-size: 0.8rem;
  cursor: pointer;
}

.state-msg { padding: 1rem; color: #475569; }
.state-msg.error { color: #991b1b; }

.save-notification {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 0.8rem;
  border-radius: 0.5rem;
  font-size: 0.85rem;
}

.save-notification-success { background: #dcfce7; color: #166534; }
.save-notification-error { background: #fee2e2; color: #991b1b; }

.validation-panel {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 0.85rem 1rem;
  font-size: 0.85rem;
}

.validation-head {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.ok { color: #166534; }

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 40;
}

.modal-container {
  background: #fff;
  border-radius: 0.75rem;
  width: min(36rem, 92vw);
  max-height: 90vh;
  overflow: auto;
}

.modal-header, .modal-footer, .modal-body { padding: 1rem 1.15rem; }
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e2e8f0;
}
.modal-header h3 { margin: 0; font-size: 1rem; }
.modal-close { background: none; border: none; cursor: pointer; color: #64748b; }
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  border-top: 1px solid #e2e8f0;
}

.file-upload-area {
  border: 2px dashed #cbd5e1;
  border-radius: 0.55rem;
  padding: 1.1rem;
  text-align: center;
}
.file-upload-area.dragging { border-color: #2563eb; background: #eff6ff; }
.upload-placeholder { color: #64748b; cursor: pointer; }
.upload-placeholder i { display: block; font-size: 1.5rem; margin-bottom: 0.35rem; }

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
}

.extract-card {
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 0.65rem;
  margin-bottom: 0.5rem;
}
.extract-card .code { font-size: 0.75rem; color: #64748b; margin-left: 0.4rem; }

@media (max-width: 900px) {
  .fields-grid, .roster-grid { grid-template-columns: 1fr; }
}
</style>
